import { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";

// ─── Staging logic ────────────────────────────────────────────────────────────

// KDIGO eGFR category (G-stage)
function gStageFromEGFR(egfr) {
  if (egfr >= 90) return "G1";
  if (egfr >= 60) return "G2";
  if (egfr >= 45) return "G3a";
  if (egfr >= 30) return "G3b";
  if (egfr >= 15) return "G4";
  return "G5";
}

const G_LABELS = {
  G1: "G1 — Normal or high (≥90)",
  G2: "G2 — Mildly decreased (60–89)",
  G3a: "G3a — Mildly to moderately decreased (45–59)",
  G3b: "G3b — Moderately to severely decreased (30–44)",
  G4: "G4 — Severely decreased (15–29)",
  G5: "G5 — Kidney failure (<15)",
};

// KDIGO albuminuria category (A-category), UACR in mg/g
function aCategoryFromUACR(uacr) {
  if (uacr < 30) return "A1";
  if (uacr <= 300) return "A2";
  return "A3";
}

const A_LABELS = {
  A1: "A1 — Normal to mildly increased (<30 mg/g)",
  A2: "A2 — Moderately increased (30–300 mg/g)",
  A3: "A3 — Severely increased (>300 mg/g)",
};

// KDIGO 2012 heat map — combines G-stage and A-category into overall risk
const KDIGO_RISK = {
  G1:  { A1: "Low risk", A2: "Moderately increased risk", A3: "High risk" },
  G2:  { A1: "Low risk", A2: "Moderately increased risk", A3: "High risk" },
  G3a: { A1: "Moderately increased risk", A2: "High risk", A3: "Very high risk" },
  G3b: { A1: "High risk", A2: "Very high risk", A3: "Very high risk" },
  G4:  { A1: "Very high risk", A2: "Very high risk", A3: "Very high risk" },
  G5:  { A1: "Very high risk", A2: "Very high risk", A3: "Very high risk" },
};

function calcKdigoRisk({ egfr, uacr }) {
  const eg = parseFloat(egfr);
  const ua = parseFloat(uacr);
  if (isNaN(eg) || isNaN(ua) || eg <= 0 || ua < 0) return null;
  const gStage = gStageFromEGFR(eg);
  const aCategory = aCategoryFromUACR(ua);
  const risk = KDIGO_RISK[gStage][aCategory];
  return { gStage, aCategory, risk, egfr: eg, uacr: ua };
}

// 2021 CKD-EPI creatinine equation (race-free) — current standard for
// estimating eGFR from a serum creatinine value.
function calcEGFRFromCreatinine({ creatinine, age, sex }) {
  const scr = parseFloat(creatinine);
  const ageNum = parseFloat(age);
  if (isNaN(scr) || isNaN(ageNum) || scr <= 0 || ageNum <= 0 || !sex) return null;
  const isFemale = sex === "female";
  const kappa = isFemale ? 0.7 : 0.9;
  const alpha = isFemale ? -0.241 : -0.302;
  const minRatio = Math.min(scr / kappa, 1);
  const maxRatio = Math.max(scr / kappa, 1);
  let egfr = 142 * Math.pow(minRatio, alpha) * Math.pow(maxRatio, -1.2) * Math.pow(0.9938, ageNum);
  if (isFemale) egfr *= 1.012;
  egfr = Math.round(egfr * 10) / 10;
  return { egfr, gStage: gStageFromEGFR(egfr) };
}

// ─── Risk badge color ─────────────────────────────────────────────────────────
const RISK_COLORS = {
  "Low risk":                    { color: "#10B981", bg: "#D1FAE5", border: "#10B981" },
  "Moderately increased risk":   { color: "#F59E0B", bg: "#FEF3C7", border: "#F59E0B" },
  "High risk":                   { color: "#F97316", bg: "#FFEDD5", border: "#F97316" },
  "Very high risk":              { color: "#991B1B", bg: "#FEE2E2", border: "#991B1B" },
};

// ─── Input field ──────────────────────────────────────────────────────────────
function BioField({ label, unit, placeholder, value, onChange, hint }) {
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.85rem", fontWeight: 600, color: "#374151" }}>{label}</Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.75rem", color: "#9CA3AF" }}>{unit}</Typography>
      </Box>
      <input
        type="number"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%", boxSizing: "border-box",
          background: "#FFFFFF",
          border: "2px solid #E5E7EB",
          borderRadius: "8px",
          padding: "12px 16px",
          color: "#1F2937",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "1rem",
          fontWeight: "500",
          outline: "none",
        }}
        onFocus={(e) => { e.target.style.borderColor = "#1A5EA8"; }}
        onBlur={(e) => { e.target.style.borderColor = "#E5E7EB"; }}
      />
      {hint && (
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.75rem", color: "#6B7280", mt: 0.75, lineHeight: 1.5 }}>{hint}</Typography>
      )}
    </Box>
  );
}

// ─── Sex toggle (used only by the eGFR-from-creatinine calculator) ────────────
function SexToggle({ value, onChange }) {
  return (
    <Box>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.85rem", fontWeight: 600, color: "#374151", mb: 1 }}>Sex</Typography>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        {["female", "male"].map((s) => (
          <Box
            key={s}
            onClick={() => onChange(s)}
            sx={{
              flex: 1, textAlign: "center", cursor: "pointer",
              px: 3, py: 1.5, borderRadius: "8px",
              border: "2px solid", borderColor: value === s ? "#1A5EA8" : "#E5E7EB",
              bgcolor: value === s ? "#EEF2FF" : "#FFFFFF",
              color: value === s ? "#1A5EA8" : "#6B7280",
              fontFamily: "'DM Sans'", fontWeight: value === s ? 600 : 500, fontSize: "0.9rem",
              textTransform: "capitalize", transition: "all 0.15s",
            }}
          >
            {s}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────
function RiskResultCard({ title, result, description }) {
  if (!result) return (
    <Box sx={{ border: "2px solid #E5E7EB", borderRadius: "12px", p: 4, bgcolor: "#F9FAFB" }}>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9CA3AF", mb: 2 }}>{title}</Typography>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.95rem", color: "#6B7280" }}>Enter values above to calculate</Typography>
    </Box>
  );

  const sc = RISK_COLORS[result.risk] || RISK_COLORS["High risk"];
  return (
    <Box sx={{
      border: "3px solid",
      borderColor: sc.border,
      borderRadius: "12px",
      p: 4,
      background: `linear-gradient(135deg, ${sc.bg} 0%, #FFFFFF 100%)`,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        width: "200px",
        height: "200px",
        background: `radial-gradient(circle, ${sc.color}15 0%, transparent 70%)`,
        borderRadius: "50%",
        transform: "translate(30%, -30%)"
      }
    }}>
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#6B7280", mb: 2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: "2rem", fontWeight: 700, color: sc.color, lineHeight: 1.15, mb: 1.5 }}>{result.risk}</Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.95rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>{G_LABELS[result.gStage]}</Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.95rem", fontWeight: 600, color: "#374151", mb: 2 }}>{A_LABELS[result.aCategory]}</Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.85rem", fontWeight: 400, color: "#6B7280", lineHeight: 1.7 }}>{description}</Typography>
      </Box>
    </Box>
  );
}

function EgfrResultCard({ result }) {
  if (!result) return (
    <Box sx={{ border: "2px solid #E5E7EB", borderRadius: "12px", p: 4, bgcolor: "#F9FAFB" }}>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9CA3AF", mb: 2 }}>Calculated eGFR</Typography>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.95rem", color: "#6B7280" }}>Enter values above to calculate</Typography>
    </Box>
  );

  const gLabel = G_LABELS[result.gStage];
  const badgeColor = result.gStage === "G1" || result.gStage === "G2" ? RISK_COLORS["Low risk"]
    : result.gStage === "G3a" ? RISK_COLORS["Moderately increased risk"]
    : result.gStage === "G3b" ? RISK_COLORS["High risk"]
    : RISK_COLORS["Very high risk"];

  return (
    <Box sx={{
      border: "3px solid",
      borderColor: badgeColor.border,
      borderRadius: "12px",
      p: 4,
      background: `linear-gradient(135deg, ${badgeColor.bg} 0%, #FFFFFF 100%)`,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        width: "200px",
        height: "200px",
        background: `radial-gradient(circle, ${badgeColor.color}15 0%, transparent 70%)`,
        borderRadius: "50%",
        transform: "translate(30%, -30%)"
      }
    }}>
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: "#6B7280", mb: 2 }}>Calculated eGFR</Typography>
        <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: "2.5rem", fontWeight: 700, color: badgeColor.color, lineHeight: 1, mb: 1.5 }}>{result.egfr} <Box component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "#6B7280" }}>mL/min/1.73m²</Box></Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "1rem", fontWeight: 600, color: "#374151", mb: 2 }}>{gLabel}</Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.85rem", fontWeight: 400, color: "#6B7280", lineHeight: 1.7 }}>
          Calculated using the 2021 CKD-EPI creatinine equation (race-free) — the current standard for estimating
          kidney function from a serum creatinine value. Add your UACR in the "KDIGO Stage &amp; Risk" tab for a
          full risk category.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BiomarkerCalculator() {
  const [vals, setVals] = useState({ egfr: "", uacr: "", creatinine: "", age: "", sex: "" });
  const [mode, setMode] = useState("KDIGO");

  const set = (key) => (v) => setVals((prev) => ({ ...prev, [key]: v }));

  const kdigoRisk = useMemo(() => calcKdigoRisk(vals), [vals]);
  const egfrFromCr = useMemo(() => calcEGFRFromCreatinine(vals), [vals]);

  const clear = () => setVals({ egfr: "", uacr: "", creatinine: "", age: "", sex: "" });

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: "#F7F9FC", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,700;1,300;1,700&display=swap');
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input::placeholder { color: #9CA3AF; }
      `}</style>

      {/* Hero */}
      <Box sx={{
        px: { xs: 3, md: 8 },
        pt: 10,
        pb: 8,
        background: "linear-gradient(135deg, #8B5CF6 0%, #1A5EA8 100%)",
        position: "relative",
        overflow: "hidden"
      }}>
        <Box sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Ccircle cx=\"1\" cy=\"1\" r=\"1\" fill=\"rgba(255,255,255,0.1)\"%3E%3C/circle%3E%3C/svg%3E')",
          opacity: 0.4
        }} />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
            <Box sx={{ width: 20, height: "2px", bgcolor: "#FFFFFF" }} />
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FFFFFF" }}>
              Staging Calculator
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "2.2rem", md: "3.4rem" }, fontWeight: 700, color: "#FFFFFF", mb: 2, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            CKD staging
          </Typography>
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "1.1rem", fontWeight: 400, color: "rgba(255,255,255,0.95)", maxWidth: 600, lineHeight: 1.75, mb: 3 }}>
            Enter your eGFR and UACR to calculate your KDIGO stage and risk category — the same system used by nephrologists.
          </Typography>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, px: 3, py: 1.5, border: "1px solid rgba(255,200,0,0.5)", borderRadius: "8px", bgcolor: "rgba(255,200,0,0.2)", backdropFilter: "blur(10px)" }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.85rem", fontWeight: 500, color: "#FFFFFF", lineHeight: 1.6 }}>
              ⚠️ For educational reference only. Staging should always be interpreted by your care team in clinical context.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mode selector */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: 6, bgcolor: "#FFFFFF" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 8, flexWrap: "wrap" }}>
          {[
            { key: "KDIGO", label: "KDIGO Stage & Risk", color: "#1A5EA8", bgColor: "#EEF2FF" },
            { key: "EGFR", label: "eGFR from Creatinine", color: "#8B5CF6", bgColor: "#F5F3FF" }
          ].map((m) => (
            <Box key={m.key} onClick={() => { setMode(m.key); clear(); }}
              sx={{
                px: 5,
                py: 2.5,
                cursor: "pointer",
                border: "3px solid",
                borderRadius: "12px",
                borderColor: mode === m.key ? m.color : "#E5E7EB",
                background: mode === m.key ? `linear-gradient(135deg, ${m.bgColor} 0%, ${m.color}10 100%)` : "#FFFFFF",
                color: mode === m.key ? m.color : "#6B7280",
                fontFamily: "'DM Sans'",
                fontWeight: mode === m.key ? 700 : 500,
                fontSize: "1rem",
                transition: "all 0.2s",
                boxShadow: mode === m.key ? `0 4px 12px ${m.color}30` : "0 2px 4px rgba(0,0,0,0.05)",
                "&:hover": {
                  borderColor: m.color,
                  transform: "translateY(-2px)"
                }
              }}
            >
              {m.label}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 8 }}>
          {/* Input panel */}
          <Box sx={{ bgcolor: "#F7F9FC", p: 5, borderRadius: "16px", border: "2px solid #E5E7EB" }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#1A5EA8", mb: 4 }}>
              📊 Lab values
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {mode === "KDIGO" && (
                <>
                  <BioField
                    label="eGFR" unit="mL/min/1.73m²"
                    placeholder="e.g. 55"
                    value={vals.egfr} onChange={set("egfr")}
                    hint="From your most recent blood test"
                  />
                  <BioField
                    label="UACR (urine albumin-to-creatinine ratio)" unit="mg/g"
                    placeholder="e.g. 45"
                    value={vals.uacr} onChange={set("uacr")}
                    hint="From your most recent urine test"
                  />
                </>
              )}
              {mode === "EGFR" && (
                <>
                  <BioField
                    label="Serum Creatinine" unit="mg/dL"
                    placeholder="e.g. 1.1"
                    value={vals.creatinine} onChange={set("creatinine")}
                    hint="From your most recent blood test"
                  />
                  <BioField
                    label="Age" unit="years"
                    placeholder="e.g. 58"
                    value={vals.age} onChange={set("age")}
                  />
                  <SexToggle value={vals.sex} onChange={set("sex")} />
                </>
              )}
            </Box>

            <Box
              onClick={clear}
              sx={{
                mt: 4,
                px: 4,
                py: 2,
                bgcolor: "#FFFFFF",
                border: "2px solid #E5E7EB",
                borderRadius: "8px",
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { borderColor: "#1A5EA8", bgcolor: "#F7F9FC" },
                transition: "all 0.2s"
              }}
            >
              <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.9rem", fontWeight: 600, color: "#6B7280" }}>
                Clear all values
              </Typography>
            </Box>
          </Box>

          {/* Results panel */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {mode === "KDIGO" && (
              <RiskResultCard
                title="KDIGO Stage & Risk"
                result={kdigoRisk}
                description="Combines your eGFR category and albuminuria category into the KDIGO risk heat map, used to guide monitoring frequency and treatment intensity."
              />
            )}
            {mode === "EGFR" && (
              <EgfrResultCard result={egfrFromCr} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Info section */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: 10, bgcolor: "#F7F9FC" }}>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "#1A5EA8", mb: 2 }}>
          Clinical note
        </Typography>
        <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, color: "#1F2937", mb: 3 }}>
          Understanding your results
        </Typography>
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "1rem", fontWeight: 400, color: "#6B7280", lineHeight: 1.8, maxWidth: 800 }}>
          KDIGO staging combines how well your kidneys are filtering (eGFR) with how much protein is leaking into
          your urine (UACR) into a single risk category. Higher risk generally means more frequent monitoring and
          more aggressive kidney-protective treatment — but staging is just one piece of your clinical picture.
          Response to treatment, blood pressure control, and overall health matter just as much. Always discuss
          your results with your nephrologist.
        </Typography>
      </Box>
    </Box>
  );
}